import { useEffect, type ReactNode } from 'react';
import { NOMBRE_TEMA, TEMAS, type Tema } from '../nucleo/temas';

// Piezas visuales compartidas. Nada de lógica: solo cómo se ve.
// Pensado para el celular: botones grandes y contraste alto.

export function Boton({
  children,
  onClick,
  tipo = 'principal',
  deshabilitado,
  ancho,
  chico,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'principal' | 'secundario' | 'peligro' | 'texto';
  deshabilitado?: boolean;
  ancho?: boolean;
  chico?: boolean;
  type?: 'button' | 'submit';
}) {
  const estilos = {
    principal: 'bg-(--color-acento) text-(--color-sobre-acento) font-semibold',
    secundario: 'bg-(--color-panel) text-(--color-texto) border border-(--color-borde)',
    peligro: 'bg-(--color-panel) text-(--color-error) border border-(--color-error)',
    texto: 'text-(--color-acento) font-semibold',
  }[tipo];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={deshabilitado}
      className={`rounded-xl transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40
        ${estilos} ${ancho ? 'w-full' : ''} ${chico ? 'min-h-9 px-3 text-sm' : 'min-h-12 px-5 text-base'}`}
    >
      {children}
    </button>
  );
}

export function Tarjeta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`superficie-tarjeta rounded-2xl border border-(--color-borde) bg-(--color-tarjeta) p-4 ${className}`}>
      {children}
    </section>
  );
}

/** Los títulos van en Bebas y en mayúscula, como en la FIP. */
export function Titulo({ children, extra }: { children: ReactNode; extra?: ReactNode }) {
  return (
    <h2 className="mb-3 flex items-baseline gap-2 font-titulo text-2xl uppercase">
      {children}
      {extra && <span className="font-sans text-xs normal-case tracking-normal text-(--color-apagado)">{extra}</span>}
    </h2>
  );
}

export function Aviso({ children, tono = 'info' }: { children: ReactNode; tono?: 'info' | 'alerta' | 'error' | 'bien' }) {
  const estilos = {
    info: 'border-(--color-borde) text-(--color-apagado)',
    alerta: 'border-(--color-alerta) text-(--color-alerta)',
    error: 'border-(--color-error) text-(--color-error)',
    bien: 'border-(--color-acento) text-(--color-acento)',
  }[tono];
  return (
    <p className={`rounded-xl border px-4 py-3 text-sm ${estilos}`} role="status">
      {children}
    </p>
  );
}

export function Campo({
  etiqueta,
  children,
  ayuda,
}: {
  etiqueta: string;
  children: ReactNode;
  ayuda?: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-semibold text-(--color-apagado)">{etiqueta}</span>
      {children}
      {ayuda && <span className="text-xs text-(--color-apagado)">{ayuda}</span>}
    </label>
  );
}

/** Clases para input, select y textarea: que se vean igual en todos lados. */
export const ENTRADA =
  'min-h-11 w-full rounded-xl border border-(--color-borde) bg-(--color-panel) px-3 text-base text-(--color-texto) placeholder:text-(--color-apagado)/60 focus:border-(--color-acento) focus:outline-none';

export function BotonVolver({ onClick, texto = 'Volver' }: { onClick: () => void; texto?: string }) {
  return (
    <button onClick={onClick} className="-ml-1 min-h-10 self-start px-1 text-xs font-bold uppercase text-(--color-acento)">
      ‹ {texto}
    </button>
  );
}

export function Cargando({ que = 'Cargando' }: { que?: string }) {
  return <p className="py-8 text-center text-(--color-apagado)">{que}...</p>;
}

export function Vacio({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-(--color-apagado)">{children}</p>;
}

/** Botoncitos redondos para elegir tema, cada uno pintado con su propio color. */
export function SelectorTema({
  valor,
  onChange,
  conHeredar,
}: {
  valor: Tema | null;
  onChange: (t: Tema | null) => void;
  /** Para categorías: la opción "como el torneo" (null). */
  conHeredar?: boolean;
}) {
  const opciones: (Tema | null)[] = conHeredar ? [null, ...TEMAS] : [...TEMAS];
  return (
    <div className="flex flex-wrap gap-2">
      {opciones.map((t) => (
        <button
          key={t ?? 'heredar'}
          type="button"
          data-tema={t ?? undefined}
          onClick={() => onChange(t)}
          aria-pressed={valor === t}
          className={`flex min-h-9 items-center gap-2 rounded-full border px-3 text-sm
            ${valor === t ? 'border-(--color-texto) font-semibold' : 'border-(--color-borde) text-(--color-apagado)'}
            ${t ? 'bg-(--color-fondo)' : ''}`}
        >
          {t && <span className="h-3 w-3 rounded-full bg-(--color-acento)" />}
          {t ? NOMBRE_TEMA[t] : 'Como el torneo'}
        </button>
      ))}
    </div>
  );
}

/** Pone el tema en <html>: fondo, resplandor y acentos cambian con él. */
export function useTema(tema: Tema) {
  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    const fondo = getComputedStyle(document.documentElement).getPropertyValue('--color-fondo').trim();
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', fondo || '#031b3d');
  }, [tema]);
}
